import PropTypes from 'prop-types';
import { ContentfulImageQueryDefaultProps, ContentfulImageQueryPropTypes } from '@prospectus/common/ui/ContentfulImage/propTypes';

import { shouldRedirect } from './externalRedirect';

const seoContentTypeShape = PropTypes.shape({
  pageTitle: PropTypes.string.isRequired,
  pageDescription: PropTypes.shape({
    pageDescription: PropTypes.string.isRequired,
  }),
  languageCode: PropTypes.string.isRequired,
  robotsMetaTagContent: PropTypes.string,
  pageImage: PropTypes.shape(ContentfulImageQueryPropTypes),
});

const seoContentTypeDefaultProps = {
  robotsMetaTagContent: undefined,
  pageTitle: '',
  pageDescription: {
    pageDescription: '',
  },
  languageCode: 'en',
  pageImage: ContentfulImageQueryDefaultProps,
};

const returnIfTruthyOrEmptyString = (array) => {
  for (let i = 0; i < array.length; i++) {
    if (array[i] || array[i] === '') {
      return array[i];
    }
  }
  return false;
};

const getOverriddenFields = (intl, customSeoFields = [], customMetaTags = []) => {
  const seoFields = customSeoFields.find(fields => fields.languageCode === intl.locale);
  const metaTags = customMetaTags.filter(metaTag => metaTag.languageCode === intl.locale);
  const robotsMetaTag = metaTags.find(metaTag => metaTag.name === 'robots');
  const metaTagsWithoutRobots = metaTags.filter(metaTag => metaTag.name !== 'robots');

  const configurations = {};
  configurations.title = seoFields?.pageTitle || false;
  configurations.description = seoFields?.pageDescription?.pageDescription || false;
  configurations.robotsMetaTagContent = returnIfTruthyOrEmptyString([
    seoFields?.robotsMetaTagContent, robotsMetaTag?.content]);
  configurations.metaTags = metaTagsWithoutRobots.length > 0 ? metaTagsWithoutRobots : false;
  return configurations;
};

const customMetaTagShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
});

// Fully Opened About Pages, meaning:
// - Indexable 2U Pages
// - EE courses excluded from the redirect experiment (renders about page immediately)
const openedUp2UProductUUIDs = [
  '32ab61e5-44b4-4316-ad59-9f04fc876e0a', // Artificial Intelligence Implications For Business
  '8ba64bc6-4914-4b76-96bb-b3326d5701dd', // Negotiation Programme
  '6ccf0b59-e136-4194-a3bb-eba8dfb7c8a8', // Real Estate Investment and Development
  'f877978a-89d9-4675-8473-ab6d8f10167c', // General Management Program
  '83d8b90b-40d5-443c-99fd-6ea813c19538', // Cybersecurity Managing Risk
  // Newly added EE Courses due to SEO-700 https://2u-internal.atlassian.net/browse/SEO-700
  '59100184-36be-4b3c-826c-5c3f5474ecb4', // Business Sustainability Management
  'b3f587fc-0cee-4dbd-8bac-b2687731a40d', // MBA Essentials
  '00176583-48ca-457f-abcd-0a9ccca1bd63', // Blockchain Technologies: Business Innovation and Application
  'd4faa293-9be0-406e-9861-3f8ffe3668b0', // Oxford Executive Leadership Programme
  'b30b2520-7f97-4792-a116-b82e9ffdbfab', // Sustainable Finance
  '5626713f-b6ea-48dd-aa03-12f468f6cd03', // Women’s Leadership Program: Leading Teams
  '232a36ac-f6d1-4cd1-8f55-7478ae49f35d', // Business and Climate Change: Towards Net Zero Emissions
  'fae65ccb-b081-41a7-a68e-954369c38ff1', // Real Estate Economics and Finance Online Certificate Course
  'd6214660-4433-4f50-8e83-75a387e9f5ac', // Public Policy Analysis
  '5d254d1e-ed6c-47de-a9e7-e0246853f526', // Cryptocurrency
  '48c64dbb-9395-44bc-93d9-9e651dded805', // Oxford Women's Leadership Development Programme
  'f1e7155d-81ea-4446-96a8-a0562da1934e', // FinTech
  'a626c6e3-1041-4937-94fe-d144c8355a2d', // Higher Education Teaching Certificate
  '3ce363ea-dccf-4e54-bfd2-f983fc32a1d4', // Oxford Leading Sustainable Corporations Programme
  'f8293d8a-c358-49ce-a916-c2eae6804f20', // Nutrition Science
  'ce3a934a-08ff-47da-823e-15f99cae32ef', // Artificial Intelligence in Health Care
  '0f183445-7dcd-49e2-ab60-b8fed8d8473c', // Internet of Things: Business Implications and Opportunities
  '68066787-5232-49fd-9cbd-f96c375066c8', // Neuroscience for Business
  '3cb02711-99a9-44ac-9acd-ef20cb346e43', // Data Analysis for Management
  '9c024a51-be79-4e86-81f3-d0e235828e6b', // Digital Business Strategy: Harnessing Our Digital Future
  '2e07afc0-9915-4138-a6a6-c54c54829cba', // Accelerated Management Program
  '5519d303-bf88-425a-88fa-a94fb5de8d5f', // Sustainable Supply Chain Management
  'da340230-636a-4495-9e81-4b753e273563', // Communicating for Influence and Impact
  'de12aa77-00b4-45ca-a1ab-01c47b14fb66', // Oxford Entrepreneurship: Venture Finance Programme
  'd73abeb8-08d6-4095-9998-25cb62f16a00', // Oxford Executive Strategy Programme
  'fa28881f-5a3e-46bf-a010-6865bfab6032', // Project Management Foundations
  '90f29252-222a-497e-b453-40b47f2eecd5', // Oxford Private Markets Investments Programme
  '83025a53-28e4-4e65-818e-cd0b6b50c3b4', // Health Economics and Policy
  'af23b39b-47bf-4ce3-b78b-3d4b980bf423', // Sustainable Real Estate: Creating a Better Built Environment
  '91c4ca9c-7f99-4644-8078-c89f0cb47eb4', // Data Monetization Strategy: Creating Value Through Data
  '76f525f4-cc66-435f-ba6d-7813f8aa9b43', // Digital Marketing
  '6b1262ca-c737-4207-bfc0-5b82bc1bbb95', // Digital Marketing Analytics
  'e2623046-dfa6-4f5f-bd7b-0f7f093f3f58', // Oxford Leading Strategic Projects Programme
  '39095a3b-d192-493b-a4a5-dde448d2bf84', // Leadership in an Exponentially Changing World
  'aae08bb5-6d16-47ba-bd22-d86f0aad7d44', // Data Analysis
  '8fa3006b-4e5c-4096-abc5-86473e33d661', // Property Development and Investment
  'ceade479-279d-4f69-8b63-c1eaf3acf1aa', // Managerial Finance
  '300b07a9-0e71-4499-a65a-7d5e21c95784', // Oxford Digital Marketing: Disruptive Strategy Programme
  '9e9e46fd-b5d3-4ebf-9a18-b0487a01bf8e', // Corporate Sustainability Management: Risk, Profit, and Purpose
  '276b1227-0b5b-4fe0-a612-7d56730ba818', // The Law and Economics of Mergers and Acquisitions
  'aafafda6-6bd7-4d61-b0ae-50561e611971', // Women's Leadership Program
  'e99c2886-2f4b-4156-b870-9a7fa1c4acf2', // Professional Communication: Business Writing and Storytelling
  '40701f47-5582-4841-a3c8-489d2f3a1f81', // Organizational Design for Digital Transformation
  '2606ed79-c582-4f7b-a956-b75dcadbc6cf', // Commercial Real Estate Analysis and Investment
  'ce983502-003f-47f2-bc13-1e5055afc8a8', // Business Sustainability Strategy: Technology and Management
  'a892c4c3-b034-46ba-b33b-d769844b5ef3', // Leading Diversity, Equity, and Inclusion
  'a5c14d4b-6ee5-4056-9c60-261294f704bc', // Business, International Relations and the Political Economy
  'a69081ed-907a-4755-8779-b1fa7b1e10b2', // Oxford Artificial Intelligence Programme
  '1bdbf677-b0c2-44b8-9b75-b8b682616626', // Construction Management
  '6ae2bd4c-1b40-49bc-9d56-ca08d4370738', // Sustainable Finance and Investment
  '4a53fa5f-59c3-491d-9874-7568bfae357c', // Inquiry-Driven Leadership
  'b262f820-df9c-416e-b887-629cb5fc050b', // Oxford Algorithmic Trading Programme
  '3cc6d511-1234-4eb2-972a-f6e524d275de', // Data: Law, Policy and Regulation
  '1b3a2209-dbf1-4a7c-8e64-96aef1480c5e', // Leadership and Change
  '0912928c-00cb-44ca-87a0-e0eb75584267', // Strategic Management Executive Programme
  '7445fd4f-0ce7-4373-925c-a53c9d18c107', // Pricing: Using Data to Improve Pricing Performance
  '068e365f-1a39-4e1d-81be-a7f82746e606', // Human-Computer Interaction for User Experience Design
  '4a7a7683-e03f-4c01-b797-af28b7e368ad', // Strategic Change Management
  '36741622-9b77-4ff7-9914-fd658ece6942', // Leading Sustainability: High Impact Leadership
  '8ba64bc6-4914-4b76-96bb-b3326d5701dd', // Negotiation Programme
  '09517282-3a79-4d55-9117-cae88cf2ff4a', // Social Media Strategy: Creating Engagement, Insight, and Action
  'a2a07812-e207-46f1-af94-17abfc905a78', // Healthcare Management
  'c5d90b4c-c3c8-4045-8910-6b9c5c8156a0', // Interpersonal Communication: Strategies for Executives
  'cbb17299-3740-4bfe-aa67-3ac8e36ebb9c', // Occupational Health and Safety
  '3d033908-34ff-4084-83e3-bed4bc310437', // Oxford Strategic Innovation Programme
  'daabb96f-0f39-44dd-8e3f-8098c2e768da', // Digital Marketing: A Strategic Perspective
  'fe2f341e-493d-42c4-8d0d-0661b490c2cb', // Supply Chain Management
  '87fcb2bd-d70f-4385-ac2d-f09dcf2095a5', // Machine Learning in Business
  '169f36c3-5dee-4d02-97a1-fe4c24ed8968', // Compliance Management
  '6d717d0e-cf46-40b1-81ad-98e7a54cd579', // Risk and Crisis Management
  'b7fe453e-c9cb-436a-88fd-313a047c7a36', // Oxford Executive Finance Programme
  'aaee5823-5acf-4896-88b8-e4ea8abdf341', // Advanced Project Management
  '888e2a4c-37d6-47c5-9902-0ef233bcf5cc', // Graphic Design
  '92e1e551-0956-4750-bdd4-ab895bba2a83', // Healthcare Leadership
  'b409117a-414f-46be-a04c-35f74706ebd6', // Corporate Innovation: Strategies for Leveraging Ecosystems
  '90797e81-dd22-480e-9ad9-27b0d1e3e124', // Fintech and the Future of Finance: Blockchain, Cryptocurrencies, Govcoins and the Payments Revolution
  '21e4c361-1bf9-4fe9-9b29-0284fb09ebff', // Business Systems Analysis
  'a37a037c-8b29-41cf-bf9c-454fad40fbc1', // Human Resource (HR) Management
  '193128aa-6d1a-4647-ae1b-9c4b385c29bc', // Oxford Future of Real Estate Investment Programme
  '523506ca-9e3f-4b57-bbac-332a8c20d482', // Healthcare Leadership
  '3d618c70-0482-4364-bdc1-7fd0b08c8dbc', // Beyond Smart Cities: Emerging Design and Technology
  'e648e954-8a68-4fcb-8c94-29ad90091b21', // Child Development: Behavior and Mental Health
  '920f1ffd-b810-48b8-9ef8-7f5292602026', // International Relations: China, Russia, the US and the Future of Geopolitics
  'a229af7b-0fe9-48ca-a215-8ac089a6697e', // Operations Management
  '7775db6f-d494-4f97-b189-f08827beccc7', // Real Estate Finance and Investment
  '611368bd-ca27-4be3-9d16-3b8cd4b08094', // Managing People and Teams
  '8e542a98-6a0f-4bed-aec5-fc8e8cb52f4b', // Women Leading Change: Shaping Our Future
  '8bb41a0a-6a54-4891-b819-386648da7e68', // Communicating Data Through Storytelling
  'b1cf0c2e-8282-430e-96e1-67184cc06a11', // Bookkeeping
  '0f183445-7dcd-49e2-ab60-b8fed8d8473c', // Internet of Things: Business Implications and Opportunities
  '995a9c5a-730a-4db2-bebc-c12cc72fa205', // Digital Transformation Strategy
  'ebe974df-6996-4d30-b01d-425b9107cd95', // Digital Transformation in HR
  '6ef191c3-613e-472f-8ba0-b821d2741624', // Modelling Cost-Effectiveness in Healthcare
  '3263679d-9211-425f-b886-e675dc6570b6', // Social Entrepreneurship
  '6435d163-c147-4d06-9b69-6ff0677d867a', // Oxford Climate Emergency Programme
  '8a0e546a-d096-477c-ba8a-5fd2fc51632b', // Paralegal Practitioner
  '4d7c0dc7-aba6-4312-8080-1be28a507376', // Inclusive Leadership Through Behavioural Science
  'a90b2d64-f4e3-4e07-82a8-2af60e8a6548', // Investment Management
  'd3dc62b5-531d-40a7-b44f-1acf687b1148', // Oxford Leading Professional Service Firms Programme
  '068e365f-1a39-4e1d-81be-a7f82746e606', // Human-Computer Interaction for User Experience Design
  'c79576f8-9249-4f9d-be70-5692c34cb3e7', // Human Capital Strategy: Creating a Sustainable Competitive Advantage
  '96fde769-f6ea-461d-a2e8-4dad4e56a474', // Regulation Strategy
  '53ab531c-e457-4f83-9a27-323f36ef04da', // LSE: Financial Analysis and Management Accounting
  'c1db2979-8dde-46be-bb77-631c4adc0b08', // University of Cape Town: Blockchain and Digital Currency: The Future of Money
  '8e19559f-dfd2-4e62-9fd2-5c4c143f3d96', // University of Cape Town: Fundamentals of Financial Management
  'a2b808a7-67b9-40f4-833a-bf09036d1998', // LSE: Strategic Decision-Making
  '864d5b89-7c78-420b-980f-c4ff64cd7f00', // University of Cape Town: Copy-editing
  '2502405e-c165-4089-96fd-86db78dd4f21', // University of Cape Town: Data Science with Python
  'ed5e4fda-d36e-4b88-bbec-e42dd35b5065', // University of Cape Town: Accounting
  '6a5cc0b9-bcea-408c-a167-9738614f0976', // University of Toronto: Rotman Women in Leadership
  'd14aab4d-81a2-4fc7-8b27-f799ec3fcc7e', // MIT SA+P: Data Science in Real Estate
  'b711ee2f-b755-44da-91e6-d131ea27a143', // Stanford (SCHE): Psychology of Addiction and Recovery
  '00f26a43-58bc-4170-8224-834b50123951', // University of Cape Town: Social Media Marketing
  'b3ddcf28-da4c-488b-807a-f7ed02502b93', // Oxford Saïd: Oxford Entrepreneurship: Venture Creation Programme
  'b751de2f-0a53-4409-9591-a7e4d78bf0d2', // Oxford Saïd: Oxford Enterprise Leadership Programme
  '5167e125-3a85-4dfd-b0fd-286d4d081386', // University of Cape Town: Business Development Management
  'f877978a-89d9-4675-8473-ab6d8f10167c', // SDA Bocconi: General Management Program
  'de540c28-ab58-45de-9a1c-fd21a88eafbc', // University of Cape Town: Executive and Management Coaching
  'd2d6788b-1a6e-4b82-9d67-3bed9d35996c', // University of Cape Town: Web Design
  '0c671eeb-389a-4d70-884a-5b9bf61d6abd', // University of Cape Town: Tax Law
  '46b3b21f-89a8-4d9a-9151-5b12202a5c07', // University of Cape Town: Marketing
  'e0577f43-6a26-4c9c-87fc-e8e6b39027e8', // Stanford (SCHE): Positive Psychology and Well-Being: The Psychological, Biological, and Social Aspects of Happiness
  'ac0779b0-d342-42c4-99b3-d2c2ff93f980', // LSE: Ethics of AI
  '1dae386f-b358-432b-a743-76feceb33c37', // MIT Sloan School of Management: Economics for Business
  '96311aa3-aea8-4e82-908e-5aa4f588c429', // Stanford (SCHE): Employee Wellness and Stress Management
  '9781d9a6-f90d-42a8-99bc-1b0b8aacab5c', // University of Toronto: Organizational Leadership
  '1e662d51-aca9-4e44-b2c4-8af7f18ed10c', // University of Cape Town: Import, Export, and Logistics Management
  'f0f62e38-3a9a-4688-84b2-26c61ab37088', // University of Cape Town: Energy Efficiency and Sustainability
  '93b36f93-9243-448c-887c-b6a5fc487df6', // LSE: Machine Learning: Practical Applications
  '9f150859-a9c6-49b8-9d9c-cca33c9794ee', // IMD: Strategies for Supply Chain Digitalization
  '26b9ba9b-c5aa-41bc-8803-4903a0cdecff', // UC Davis: Leadership Program
  '7a20a18a-7769-4fd3-8171-21f1ea8bc059', // University of Cape Town: Payroll and Tax Administration
  '7db051ef-3bb0-425f-b5ff-0290468e6cad', // University of Cape Town: Agile Project Management
  'f1ca0ab1-af12-4b12-b033-dd3f69cee09f', // Yale SOM Executive Education: Leadership for Global Business and Politics
  '996ab9cb-5edd-4f23-9c4f-0bcaf6c2e83d', // University of Cape Town: Compliance Essentials
  '644e6a8e-0890-47c2-8c6f-65122b4d490d', // MIT SA+P: Real Estate Financial Analytics
  'ebd51153-c131-41f4-958a-da5515096d5e', // University of Cape Town: Commercial and Contract Law
  '7fae2be9-303f-4095-bacd-79b1128445e6', // University of Cape Town: Training and Development Management
  'bfa0900f-4692-4fee-89e5-2fd52754b445', // University of Cape Town: Business Analytics
  'd7e8d061-37e6-4183-a5c0-fa156cb8a7d8', // University of Cape Town: IT Management
  'e1a04c62-fe42-46c6-ace9-0acb5b806049', // Rice University: Data Analysis and Visualization
  'd8f6d0cd-7291-43e3-8339-d530561f438c', // IMD: Developing Digital Transformation Strategies
  'f8609732-2015-4cba-9dbd-b3e1e4716ab6', // LSE: Public Policy Innovations
  'aad96774-97c0-4600-86b4-43ac40c3fc82', // Yale SOM Executive Education: Negotiation Strategies
  'a1440f2d-fc3e-413e-bd36-9e3c68ff5f7c', // University of Cape Town: Financial Modelling and Analysis
  'e7a7a2a2-aead-4e1b-b563-b73cb15af503', // University of Cape Town: Business Risk Management
  '9816dd0e-3dc4-4180-8db8-e2091c0317a8', // Stanford (SCHE): Exercise Physiology
  'de777df3-e676-447a-a630-3380352991f3', // University of Cape Town: Sales Management
  '1e597737-2ca1-45ba-9663-24cd3a481021', // University of Cape Town: Practical Labour Law
  '718fe6ad-3cf7-4f15-a938-781f4d2da59f', // University of Cape Town: User Experience Design
  '9564e9a2-243e-4476-b2b5-47b2293cb861', // IMD: Organizational Leadership: Driving Culture and Performance
  '8d82ec70-aa3b-4fab-aa78-9264132257db', // HarvardX: Global Health Delivery
  'e8c256c7-7508-4041-abc8-57c5acda2a08', // University of Cape Town: Instructional Design
  '83f7e9c6-ef95-416d-b570-22d49d3fc94e', // University of Cape Town: Professional Communication and Office Management
  '5d5d45a9-c5c1-4205-8c5f-271ec65cded1', // MIT SA+P: Real Estate Development: Planning and Process
  '49674ac0-ddf5-4ca7-960b-228023c5d0c1', // CISL: Sustainable Food: Production and Processing
  'a9d0cba2-047b-4f28-b6ac-fc8260934bbf', // MIT Sloan School of Management: Managing the Human Side of Digital Disruption
  'ed79a49b-64c1-48d2-afdc-054bf921e38d', // HarvardX: CRISPR: Gene-editing Applications
  '06bd214b-c793-4fa0-9fec-65a12708504c', // University of Cape Town: Facilities Management
  'afa1450c-3e4e-4afb-98bb-eedfdcf69d15', // Oxford Saïd: Oxford Leading Through Uncertainty and Disruption: Building Resilient Organisations Programme
  '59c01d61-8a95-4720-ae68-20a76637ba46', // University of Cape Town: Executive Support
  '9cd5bf60-2133-40f0-a242-c2af9b0adb32', // Rice University: Strategic Project Management
  'aed20180-43f9-425f-967c-5d139a4aec49', // University of Cape Town: Product Management
  '151f9c65-9376-4696-9676-d66327f5d477', // SDA Bocconi: Sustainable Fashion: Brand and Business Strategy Program
  'f484cc2f-ee4b-4e94-ba82-476d0f62b4f3', // Yale SOM Executive Education: Leading Global Virtual Teams
  'b538f688-0d2f-433c-8cea-3797730dc67a', // IMD: Cybersecurity Risk and Strategy
  '605d4841-19b0-40d6-92ab-23c8b97f1fd5', // UCT Graduate School of Business: Digital Transformation Strategy
  '0a8ba50d-a4c6-4b32-95b6-6f7bda4b0b28', // MIT Sloan School of Management: Management Analytics: Decision-Making Lessons From the Sports Industry
  '5fb664b2-1b3f-4d58-8131-f4f6126fcc00', // University of Cape Town: Fundamentals of Cybersecurity
  'c2a36917-7f76-4ee0-b5e9-a967f4d06067', // SDA Bocconi: Sales Management Program
  'd2beac4c-f0cf-4e1f-8940-8f199daa1336', // University of Cape Town: Change Management
  'fedae69d-4418-4494-bd8e-87cef5dd205f', // Stanford (SCHE): Psychology for High Performance
  '1c33bd24-a12f-4215-acd0-528582201854', // LSE: Entrepreneurial Finance
  '0baa84cf-da71-4546-89ae-3c5394ae8614', // University of Cape Town: Public Relations (PR)
  '47a5ebbd-a8bc-4938-a3f3-427e25c4b885', // Northwestern University: Digital Marketing and Media Strategy
  'f454834e-1e74-4c56-b334-f9ccb14ce561', // Northwestern University: Customer Experience Innovation
  '870a8e78-5fac-4f74-8577-ad5878488965', // GetSmarter: Speaking in Public: Storytelling, Influence, and Presence
  'd3b8a672-a162-48dc-934d-be8b143a1949', // University of Cape Town: Search Engine Optimisation
  'c2845b1d-e891-4d5c-a635-4aa07f984692', // Northwestern University: MarTech Strategy: Leveraging Data and Technology in Marketing
  'e16da2b7-559f-47bf-83e9-01244e1e3152', // University of Cape Town: Cost and Management Accounting
  'df3639e0-3609-42fe-b24d-b74938087ea6', // CISL: Sustainable Marketing, Media and Creative
  '3d6257b0-8fb7-41d7-91d8-48718eb556fb', // University of Cape Town: Managing Technical Professionals
  'c290932d-9679-448f-b9ce-ae508ff340f8', // University of Cape Town: Fintech: Disruption in Finance
  '716a9b52-5558-4552-b4f9-815db41e8619', // University of Cape Town: Data Protection and Privacy
  'e45aacbe-4bcd-47a4-b88f-488fa5721b2c', // University of Cape Town: Copywriting for Digital Marketing
  'c2c518c0-3225-4bb3-ac5f-1a9b03fcb61a', // University of Cape Town: Small Business: Growth and Management
];

const indexableTypes = {
  degree: 'degree',
  exEdCourse: 'exEdCourse',
};
const isIndexableByRobots = (uuid, type = '') => {
  switch (type) {
    case indexableTypes.degree:
      return !shouldRedirect(uuid);
    case indexableTypes.exEdCourse:
      return openedUp2UProductUUIDs.includes(uuid);
    default:
      return false;
  }
};

const publisher = {
  '@type': 'Organization',
  name: 'edX',
  url: 'edx.org',
};

export {
  seoContentTypeShape,
  seoContentTypeDefaultProps,
  getOverriddenFields,
  customMetaTagShape,
  isIndexableByRobots,
  indexableTypes,
  publisher,
};
